'use client';

import React, { useState } from 'react';
import { TrendingUp, ChevronRight, Play, Film } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { useClips } from '@/lib/useClips';

export default function TrendingBanner() {
  const [hovered, setHovered] = useState(false);
  const { clips, loading } = useClips({ sort: 'trending', limit: 1 });
  const featuredClip = clips?.[0] ?? null;

  if (loading) {
    return (
      <div
        className="rounded-2xl animate-pulse"
        style={{ minHeight: 200, background: 'var(--muted)', border: '1px solid var(--border)' }}
      />
    );
  }

  if (!featuredClip) {
    return (
      <div
        className="relative rounded-2xl overflow-hidden flex items-center justify-center"
        style={{ minHeight: 200, background: 'var(--muted)', border: '1px dashed var(--border)' }}
      >
        <div className="flex flex-col items-center gap-3 text-center px-6">
          <Film size={40} style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
          <p className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>
            No featured clips yet — be the first creator to upload a clip!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300"
      style={{ border: '1px solid var(--border)', minHeight: '200px' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <AppImage
          src={featuredClip?.thumbnail}
          alt={`Featured clip: ${featuredClip?.title}`}
          fill
          priority
          className="object-cover transition-transform duration-700"
          style={{ transform: hovered ? 'scale(1.04)' : 'scale(1)' }}
          sizes="(max-width: 1536px) 100vw, 1536px"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(90deg, rgba(10,10,15,0.92) 0%, rgba(10,10,15,0.6) 50%, rgba(10,10,15,0.2) 100%)' }}
        />
      </div>
      {/* Content */}
      <div className="relative z-10 p-6 sm:p-8 flex flex-col justify-between h-full" style={{ minHeight: '200px' }}>
        <div className="flex items-center gap-2 mb-3">
          <span
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
            style={{ background: 'rgba(255, 107, 53, 0.2)', color: '#FF6B35', border: '1px solid rgba(255, 107, 53, 0.4)' }}
          >
            <TrendingUp size={11} />
            #1 Trending Today
          </span>
          <span
            className="px-2 py-1 rounded-full text-xs font-medium"
            style={{ background: 'rgba(108, 99, 255, 0.2)', color: 'var(--primary)', border: '1px solid rgba(108, 99, 255, 0.3)' }}
          >
            {featuredClip?.category}
          </span>
        </div>

        <div className="max-w-2xl">
          <h2
            className="font-bold text-xl sm:text-2xl lg:text-3xl leading-tight mb-2"
            style={{ color: 'var(--foreground)' }}
          >
            {featuredClip?.title}
          </h2>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="badge-resolution">{featuredClip?.resolution}</span>
              <span className="badge-fps">{featuredClip?.fps}fps</span>
            </div>
            <span className="text-sm font-medium" style={{ color: 'rgba(240, 240, 248, 0.6)' }}>
              by {featuredClip?.uploader}
            </span>
            <span className="text-sm font-semibold" style={{ color: 'var(--accent)' }}>
              {featuredClip?.downloads?.toLocaleString()} downloads
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-5">
          <button className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm">
            <Play size={14} fill="white" />
            Watch Preview
          </button>
          <button className="btn-ghost flex items-center gap-2 px-5 py-2.5 text-sm">
            Download Free <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}